/**
 * Error Boundary for Label Layout Editor
 *
 * Catches React errors within the Label Layout Editor and displays
 * a user-friendly fallback UI with recovery options.
 *
 * Features:
 * - Reset button to clear store state and retry
 * - Reload page option for hard recovery
 * - Development-only error details
 * - Sentry error reporting
 */
import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
import { useLabelLayoutStore } from './label-layout-store';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for the Label Layout Editor.
 * Provides options to reset the editor state or reload the page.
 */
export class LabelLayoutErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('LabelLayoutErrorBoundary caught error:', error, errorInfo);
    }

    // Report to Sentry
    Sentry.captureException(error, (scope) => {
      scope.setContext('react', {
        componentStack: errorInfo.componentStack || null,
      });
      scope.setContext('business', {
        operation: 'label_layout_editor',
        component: 'LabelLayoutErrorBoundary',
      });
      scope.setTag('errorBoundary', 'LabelLayoutErrorBoundary');
      scope.setTag('feature', 'production-settings');
      scope.setLevel('error');
      return scope;
    });
  }

  handleReset = () => {
    // Reset the Zustand store to initial state
    useLabelLayoutStore.getState().reset();

    // Clear error state and try to re-render
    this.setState({ hasError: false, error: null });

    // Call optional callback
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-destructive">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-destructive/10 p-4 mb-4">
              <AlertTriangle
                className="h-12 w-12 text-destructive"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              Editor Error
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              The Label Layout Editor encountered an error. You can try resetting the editor to its default state, or reload the page if the problem persists.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="mb-6 p-4 bg-muted rounded-lg text-left text-xs overflow-auto max-w-full max-h-32 w-full">
                {this.state.error.message}
                {this.state.error.stack && (
                  <>
                    {'\n\n'}
                    {this.state.error.stack}
                  </>
                )}
              </pre>
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={this.handleReset}
                className="h-12 gap-2"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Reset Editor
              </Button>
              <Button
                variant="default"
                onClick={this.handleReload}
                className="h-12 gap-2"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Reload Page
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
